import axios from 'axios';
import { toast } from 'react-toastify';
import { loginRequest } from '../authConfig';
import store from '../redux/store';
import { setToken } from '../redux/slices/userSlice';
import { msalInstance } from '../index';

axios.defaults.withCredentials = true;

export const client = axios.create({
  // baseURL: 'http://localhost:4000/api',
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercept all requests - attach Access Token from Redux store
client.interceptors.request.use(
  async (config) => {
    // Skip if Authorization header is already explicitly set (e.g., login with MSAL token)
    if (!config.headers.Authorization) {
      const state = store.getState();
      const token = state.user?.token;

      if (token && token !== "error") {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    config.headers.version = "1.0.3";
    return config;
  },
  (error) => Promise.reject(error)
);

// Intercept all responses
client.interceptors.response.use(
  async response => {
    return response;
  },
  async (error) => {
    // Network error (server down, no internet, etc.)
    if (!error.response) {
      console.error('[NETWORK ERROR]', error.message);
      toast.error("Network error. Please check your connection.");
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    console.log('[API ERROR] CODE:', status, 'MESSAGE:', data?.error || data?.message);

    if (status === 401) {
      const originalRequest = error.config;
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        // Step 1: Try Refresh Token
        const state = store.getState();
        const refreshToken = state.user?.refreshToken;
        if (refreshToken) {
          try {
            const refreshResponse = await client.post('/data/refresh', { refreshToken }, {
              headers: { Authorization: '' }
            });
            const newToken = refreshResponse.data.token;
            store.dispatch(setToken({ token: newToken }));
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return client(originalRequest);
          } catch (refreshError) {
            console.error('[Refresh Token Failed]', refreshError);
          }
        }

        // Step 2: Refresh failed → Try MSAL re-authentication
        try {
          const currentAccount = msalInstance.getActiveAccount();
          if (currentAccount) {
            const msalResponse = await msalInstance.acquireTokenSilent({
              ...loginRequest,
              account: currentAccount
            });
            const loginResponse = await login(msalResponse.accessToken);
            store.dispatch(setToken({ token: loginResponse.token, refreshToken: loginResponse.refreshToken }));
            originalRequest.headers.Authorization = `Bearer ${loginResponse.token}`;
            return client(originalRequest);
          }
        } catch (msalError) {
          console.error('[MSAL Refresh Failed]', msalError);
        }
      }
      toast.error("Session expired. Please log in again.");
      store.dispatch(setToken({ token: "error", refreshToken: '' }));
    } else if (status === 403) {
      toast.info("The application has been updated. Reloading...");
      setTimeout(() => window.location.reload(), 1500);
    } else if (status >= 500) {
      toast.error("Server error. Please try again later.");
    }

    return Promise.reject(error);
  },
);

export const login = async (msalToken) => {
  try {
    const response = await client.post('/data/login', {}, {
      headers: { Authorization: `Bearer ${msalToken}` }
    });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error ?? err.message);
  }
};

export const validateToken = async () => {
  try {
    const response = await client.get('/data/validateToken');
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.error ?? err.message);
  }
};

export const getSummary = async () => {
  try {
    const response = await client.get('/data/summary', {
      params: {}
    });
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.err ?? err.message);
  }
};

export const getBuildStatusData = async (plant) => {
  try {
    const response = await client.get('/data/buildStatus', {
      params: {
        plant
      }
    });
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.err ?? err.message);
  }
};

export const getProductionData = async (plant) => {
  try {
    const response = await client.get('/data/production', {
      params: {
        plant
      }
    });
    return response.data ?? [];
  } catch (err) {
    throw new Error(err.response?.data?.err ?? err.message);
  }
};
